import { ActionIcon, Select } from '@mantine/core'
import { Trash2 } from 'lucide-react'

import DNButton from 'src/components/Buttons/Button/DNButton'
import {
  DocumentCategory,
  DocumentCategoryLabel,
} from 'src/graphql/types/documents'

export interface NotificationConfigRow {
  id?: string
  category: string | null
  offsetDays: number
  actionType: 'SMS' | 'EMAIL'
  recipient: 'APPLICANT' | 'EMPLOYEE'
}

interface NotificationConfigTableProps {
  configs: NotificationConfigRow[]
  editing: boolean
  onUpdate: (index: number, field: keyof NotificationConfigRow, value: string | number | null) => void
  onRemove: (index: number) => void
  onAdd: () => void
  onFillDefaults: () => void
}

const ACTION_OPTIONS = [
  { value: 'SMS', label: 'SMS' },
  { value: 'EMAIL', label: 'Email' },
]

const RECIPIENT_OPTIONS = [
  { value: 'APPLICANT', label: 'Applicant' },
  { value: 'EMPLOYEE', label: 'Employee' },
]

const CATEGORY_OPTIONS = [
  { value: '', label: 'All' },
  ...Object.values(DocumentCategory)
    .sort()
    .map((cat) => ({
      value: cat,
      label: DocumentCategoryLabel[cat] || cat,
    })),
]

const categoryLabel = (category: string | null) => {
  if (!category) return 'All'
  return DocumentCategoryLabel[category as DocumentCategory] || category
}

const NotificationConfigTable = ({
  configs,
  editing,
  onUpdate,
  onRemove,
  onAdd,
  onFillDefaults,
}: NotificationConfigTableProps) => {
  const handleRecipientChange = (index: number, config: NotificationConfigRow, val: string | null) => {
    if (!val) return
    onUpdate(index, 'recipient', val)
    if (val === 'EMPLOYEE' && config.actionType === 'SMS') {
      onUpdate(index, 'actionType', 'EMAIL')
    }
  }

  if (configs.length === 0) {
    return (
      <div className="max-w-2xl">
        <div className="flex flex-col items-center gap-2 py-6 text-center text-sm text-doubleNickel-gray-400">
          No notifications configured.
          {editing && (
            <DNButton
              variant="outline"
              onClick={onFillDefaults}
              className="text-xs"
            >
              Fill with defaults
            </DNButton>
          )}
        </div>
        {editing && (
          <button
            onClick={onAdd}
            className="mt-2 max-w-2xl rounded-md border border-dashed border-doubleNickel-gray-300 py-1.5 text-xs text-doubleNickel-brand-500 hover:border-doubleNickel-brand-500 hover:bg-doubleNickel-brand-25 w-full"
          >
            + Add notification
          </button>
        )}
      </div>
    )
  }

  return (
    <div>
      <table className="w-full max-w-2xl">
        <thead>
          <tr className="border-b border-doubleNickel-gray-200">
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-doubleNickel-gray-500">
              Category
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-doubleNickel-gray-500">
              Days Before
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-doubleNickel-gray-500">
              Action
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-doubleNickel-gray-500">
              Recipient
            </th>
            {editing && <th className="w-8" />}
          </tr>
        </thead>
        <tbody>
          {configs.map((config, index) => (
            <tr
              key={config.id || index}
              className="border-b border-doubleNickel-gray-100 hover:bg-doubleNickel-gray-50"
            >
              {editing ? (
                <>
                  <td className="w-44 px-3 py-1.5">
                    <Select
                      size="xs"
                      value={config.category || ''}
                      onChange={(val) => onUpdate(index, 'category', val || null)}
                      data={CATEGORY_OPTIONS}
                      searchable
                      classNames={{ input: 'text-xs' }}
                    />
                  </td>
                  <td className="w-20 px-3 py-1.5">
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={config.offsetDays}
                      onChange={(e) =>
                        onUpdate(index, 'offsetDays', parseInt(e.target.value) || 1)
                      }
                      className="w-full rounded border border-doubleNickel-gray-300 px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="w-28 px-3 py-1.5">
                    <Select
                      size="xs"
                      value={config.actionType}
                      onChange={(val) => onUpdate(index, 'actionType', val)}
                      allowDeselect={false}
                      data={
                        config.recipient === 'EMPLOYEE'
                          ? [{ value: 'EMAIL', label: 'Email' }]
                          : ACTION_OPTIONS
                      }
                      classNames={{ input: 'text-xs' }}
                    />
                  </td>
                  <td className="w-32 px-3 py-1.5">
                    <Select
                      size="xs"
                      value={config.recipient}
                      onChange={(val) => handleRecipientChange(index, config, val)}
                      allowDeselect={false}
                      data={RECIPIENT_OPTIONS}
                      classNames={{ input: 'text-xs' }}
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      onClick={() => onRemove(index)}
                    >
                      <Trash2 size={14} />
                    </ActionIcon>
                  </td>
                </>
              ) : (
                <>
                  <td className="px-3 py-2 text-sm">
                    <span className="rounded-full bg-doubleNickel-gray-100 px-2 py-0.5 text-xs font-medium text-doubleNickel-gray-700">
                      {categoryLabel(config.category)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm text-doubleNickel-gray-900">
                    {config.offsetDays} days
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        config.actionType === 'SMS'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {config.actionType}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm text-doubleNickel-gray-900">
                    {config.recipient === 'APPLICANT' ? 'Applicant' : 'Employee'}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <button
          onClick={onAdd}
          className="mt-2 w-full max-w-2xl rounded-md border border-dashed border-doubleNickel-gray-300 py-1.5 text-xs text-doubleNickel-brand-500 hover:border-doubleNickel-brand-500 hover:bg-doubleNickel-brand-25"
        >
          + Add notification
        </button>
      )}
    </div>
  )
}

export default NotificationConfigTable
